import { useEffect, useRef, useState } from "react";

/**
 * A PDF rendered into this page, as pictures.
 *
 * Not an iframe, and not a link. Both hand the document to the browser's own
 * PDF viewer, and on a phone that viewer takes the whole screen with no way
 * back to the button the signer was meant to press next. Android Chrome will
 * not render a PDF in an iframe at all — it offers a download instead — and
 * iOS Safari shows the first page and will not scroll, which is why a link out
 * sat beside the frame. That link was the trap.
 *
 * So the pages are drawn onto canvases that belong to this document. They
 * scroll like anything else on the page, the signing button stays in the
 * layout underneath, and there is nowhere for the signer to get lost. Nothing
 * navigates.
 *
 * Two things keep it from falling over on the phone that is actually being
 * used. The canvases are sized from the box they are displayed in rather than
 * from the paper, and only the pages near the viewport hold a bitmap — the
 * rest are empty boxes of the right shape. A canvas costs width × height × 4
 * bytes whether or not anyone is looking at it, so a nine-page packet rendered
 * eagerly at print resolution is a couple of hundred megabytes and a tab that
 * dies halfway through signing.
 *
 * pdf.js is loaded on demand. Only the customer reading their paperwork needs
 * it; nobody on the dealership side pays for it on page load.
 */

/** Device pixels across, at most. A desktop box is ~760 CSS px, so this is
    still past 2x there and well past it on a phone. */
const MAX_WIDTH_PX = 1600;

/** How far outside the viewport a page starts drawing. One screen either way:
    enough that a normal scroll never catches up with the renderer. */
const NEAR = "150% 0px";

/** Pages that keep their bitmap. Beyond this they are dropped and redrawn if
    the signer scrolls back, which is fast and costs nothing to look at. */
const KEEP = 6;

type State =
  | { kind: "loading" }
  | { kind: "ready"; pages: number }
  | { kind: "failed"; message: string };

export default function PdfPages({ url, onState }: {
  url: string;
  /** Reported upward so the page can refuse to let anyone past a document
      they have not been shown. A signer who cannot see the paperwork must not
      be able to sign it, which is the whole reason this is lifted out. */
  onState?: (s: { kind: "loading" } | { kind: "ready"; pages: number }
                | { kind: "failed" }) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<State>({ kind: "loading" });

  /* In a ref so the effect does not re-run — and tear the document down —
     every time the parent re-renders with a fresh callback. */
  const report = useRef(onState);
  report.current = onState;

  useEffect(() => {
    let live = true;
    let doc: { destroy(): Promise<void> } | null = null;
    let observer: IntersectionObserver | null = null;

    (async () => {
      setState({ kind: "loading" });
      report.current?.({ kind: "loading" });
      try {
        const pdfjs = await import("pdfjs-dist");
        /* The worker ships as its own file. Vite rewrites this to the built
           asset's URL, so it is served from our own origin rather than a CDN
           — which matters because the signing page has to work even where
           nothing third party is reachable. */
        const workerUrl = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

        const loaded = await pdfjs.getDocument({ url, isEvalSupported: false }).promise;
        if (!live) { await loaded.destroy(); return; }
        doc = loaded;

        const count = loaded.numPages;
        const mount = host.current;
        if (!mount) return;
        mount.replaceChildren();

        /* Measured from the box the pages are shown in, not from the paper.
           Falls back to the viewport on the first paint, when the box may not
           have been laid out yet. */
        const boxWidth = mount.clientWidth || window.innerWidth || 390;
        const dpr = window.devicePixelRatio || 1;
        const targetWidth = Math.min(Math.round(boxWidth * dpr), MAX_WIDTH_PX);

        /* Every page gets its box up front, at the right shape, so the
           scrollbar is honest from the first moment and nothing jumps as
           pages fill in. */
        const slots: { canvas: HTMLCanvasElement; n: number }[] = [];
        for (let n = 1; n <= count; n++) {
          const page = await loaded.getPage(n);
          if (!live) return;
          const natural = page.getViewport({ scale: 1 });
          const canvas = document.createElement("canvas");
          canvas.className = "pdfpage";
          canvas.style.aspectRatio = `${natural.width} / ${natural.height}`;
          canvas.setAttribute("role", "img");
          canvas.setAttribute("aria-label", `Page ${n} of ${count}`);
          mount.appendChild(canvas);
          slots.push({ canvas, n });
          page.cleanup();
        }

        setState({ kind: "ready", pages: count });
        report.current?.({ kind: "ready", pages: count });

        const drawn = new Map<number, HTMLCanvasElement>();
        const drawing = new Set<number>();
        /* Scroll order, so the pages dropped first are the ones furthest from
           where the signer is reading. */
        const recent: number[] = [];

        const release = (n: number) => {
          const c = drawn.get(n);
          if (!c) return;
          /* Zeroing the dimensions is what actually frees the bitmap; clearing
             the context does not. */
          c.width = 0; c.height = 0;
          drawn.delete(n);
        };

        const draw = async (slot: { canvas: HTMLCanvasElement; n: number }) => {
          if (!live || drawn.has(slot.n) || drawing.has(slot.n)) return;
          drawing.add(slot.n);
          try {
            const page = await loaded.getPage(slot.n);
            if (!live) return;
            const natural = page.getViewport({ scale: 1 });
            const viewport = page.getViewport({ scale: targetWidth / natural.width });
            slot.canvas.width = Math.floor(viewport.width);
            slot.canvas.height = Math.floor(viewport.height);
            const ctx = slot.canvas.getContext("2d");
            if (!ctx) return;
            await page.render({ canvas: slot.canvas, canvasContext: ctx, viewport }).promise;
            page.cleanup();
            if (!live) return;

            drawn.set(slot.n, slot.canvas);
            recent.push(slot.n);
            while (recent.length > KEEP) release(recent.shift()!);
          } catch {
            /* One page failing is not the document failing. The signer sees a
               blank box in a packet that otherwise reads, which is honest, and
               the ones around it still render. */
          } finally {
            drawing.delete(slot.n);
          }
        };

        observer = new IntersectionObserver((entries) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue;
            const slot = slots.find((s) => s.canvas === e.target);
            if (slot) void draw(slot);
          }
        }, { root: mount.parentElement, rootMargin: NEAR });

        for (const s of slots) observer.observe(s.canvas);
      } catch (e) {
        if (!live) return;
        setState({
          kind: "failed",
          message: e instanceof Error ? e.message : "The document would not open.",
        });
        report.current?.({ kind: "failed" });
      }
    })();

    return () => {
      live = false;
      observer?.disconnect();
      void doc?.destroy();
    };
  }, [url]);

  return (
    <div className="pdfpages">
      {state.kind === "loading" && (
        <p className="hint dim pdfstatus">Loading your paperwork</p>
      )}
      {state.kind === "failed" && (
        <p className="hint err pdfstatus">
          Your paperwork would not load. Please call the dealership — nothing
          has been signed, and you should not sign anything you have not read.
        </p>
      )}
      <div className="pdfsheets" ref={host} />
    </div>
  );
}
